import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  useBookingsByIdsNavigation,
  useRidesByIdsNavigation,
} from "@ryde/redux";
import {
  BulkProduceTypeEnum,
  IProductionStateLogData,
  onActivateKeyDown,
  resolveUnmatchedBookingsDisplay,
  useDrawers,
} from "@ryde/shared";

import { ProductionLogsStatsTabsMetricsHub } from "./production-logs.stats.tabs.metrics-hub";

interface IProductionLogsStatsTabsMetricsStats {
  state?: IProductionStateLogData;
}

export const ProductionLogsStatsTabsMetricsStats: FC<
  IProductionLogsStatsTabsMetricsStats
> = ({ state }) => {
  const {
    action,
    producedAndJoinedRideIds,
    producedSavedRideIds,
    joinedRideIds,
    joinedBookingIds,
    allInputBookingIds,
    producedRideCount,
    totalBookingCount,
    bookingsAlreadyInProduceCount,
    bookingsAlreadyInProduceIds,
    joinedRideCount,
    joinedBookingCount,
    producedBookingCount,
    producedBookingIds,
    uniqueProducedBookingIds,
    uniqueJoinedBookingIds,
    failedToSaveProducedRidesCount,
    rideTooShortBookingCount,
    rideTooShortBookingIds,
    notFoundRidesToJoinBookingCount,
    notFoundRidesToJoinBookingIds,
    failedToSaveJoinedRideCount,
    failedToSaveJoinedRideIds,
    failedToSaveJoinedBookingCount,
    failedToSaveJoinedBookingIds,
    joinedSentRideSupplierCanceledIds,
    joinedSentRideSupplierCanceledCount,
    joinedSentRideSupplierUpdatedIds,
    joinedSentRideSupplierUpdatedCount,
    courseJoinedSentRideSupplierCanceledIds,
    courseJoinedSentRideSupplierCanceledCount,
    courseJoinedSentRideSupplierUpdatedIds,
    courseJoinedSentRideSupplierUpdatedCount,
    coursesSkippedOnNoPassengersCount,
    coursesSkippedOnNoPassengers,
    coursesAlreadyExistCount,
    coursesAlreadyExist,
    producedCoursesCount,
    producedCourses,
    notSavedCoursesCount,
    notSavedCourses,
    skippedPassengerBookingCount,
    skippedPassengerBookingIds,
    coursePreSavedRideIds,
  } = state || {};
  const { t } = useTranslation();
  const { navigateToRidesByIds } = useRidesByIdsNavigation();
  const { navigateToBookingsByIds } = useBookingsByIdsNavigation();
  const { openCourseSchedulesPreviewDrawer } = useDrawers();

  const producedRideIdsComputed = useMemo(() => {
    if (!producedAndJoinedRideIds?.length) {
      return [];
    }
    const joinedRideIdsSet = new Set(joinedRideIds || []);
    return producedAndJoinedRideIds.filter(id => !joinedRideIdsSet.has(id));
  }, [producedAndJoinedRideIds, joinedRideIds]);
  const producedRideIds = producedSavedRideIds ?? producedRideIdsComputed;

  const {
    unmatchedBookingsDisplayCount,
    unmatchedBookingsDisplayIds,
    unmatchedBookingsLabelKey,
  } = useMemo(
    () =>
      resolveUnmatchedBookingsDisplay({
        totalBookingCount,
        failedToSaveJoinedBookingCount,
        joinedBookingCount,
        producedBookingCount,
        rideTooShortBookingCount,
        notFoundRidesToJoinBookingCount,
        skippedPassengerBookingCount,
        allInputBookingIds,
        joinedBookingIds,
        producedBookingIds,
        rideTooShortBookingIds,
        notFoundRidesToJoinBookingIds,
        failedToSaveJoinedBookingIds,
        skippedPassengerBookingIds,
      }),
    [
      totalBookingCount,
      failedToSaveJoinedBookingCount,
      joinedBookingCount,
      producedBookingCount,
      rideTooShortBookingCount,
      notFoundRidesToJoinBookingCount,
      skippedPassengerBookingCount,
      allInputBookingIds,
      joinedBookingIds,
      producedBookingIds,
      rideTooShortBookingIds,
      notFoundRidesToJoinBookingIds,
      failedToSaveJoinedBookingIds,
      skippedPassengerBookingIds,
    ],
  );

  const shouldShowJoinDetails =
    action === BulkProduceTypeEnum.Both || action === BulkProduceTypeEnum.Join;

  const shouldShowCourseSupplierSyncStats =
    action === BulkProduceTypeEnum.Both ||
    action === BulkProduceTypeEnum.Produce;

  return (
    <div className="log__metrics-section">
      <div className="log__metrics-section-title">
        Produce & Join Statistics
      </div>
      <div className="log__metrics-map-stats">
        {!!bookingsAlreadyInProduceCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              Already in produce:
            </span>
            {bookingsAlreadyInProduceIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToBookingsByIds(bookingsAlreadyInProduceIds)
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(bookingsAlreadyInProduceIds),
                )}
              >
                {bookingsAlreadyInProduceCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-booking">
                {bookingsAlreadyInProduceCount}
              </span>
            )}
          </div>
        )}
        {!!failedToSaveProducedRidesCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">Not saved rides:</span>
            <span className="log__metrics-map-value">
              {failedToSaveProducedRidesCount}
            </span>
          </div>
        )}
        {!!totalBookingCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              Total bookings:
            </span>
            {allInputBookingIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() => navigateToBookingsByIds(allInputBookingIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(allInputBookingIds),
                )}
              >
                {totalBookingCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-booking">
                {totalBookingCount}
              </span>
            )}
          </div>
        )}
        {shouldShowJoinDetails && !!joinedBookingCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              Joined bookings:
            </span>
            {(uniqueJoinedBookingIds ?? joinedBookingIds)?.length ? (
              <span
                className="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToBookingsByIds(
                    uniqueJoinedBookingIds ?? joinedBookingIds ?? [],
                  )
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(
                    uniqueJoinedBookingIds ?? joinedBookingIds ?? [],
                  ),
                )}
              >
                {joinedBookingCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-booking">
                {joinedBookingCount}
              </span>
            )}
          </div>
        )}
        {shouldShowJoinDetails && !!joinedRideCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">Joined rides:</span>
            {joinedRideIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-green"
                role="button"
                tabIndex={0}
                onClick={() => navigateToRidesByIds(joinedRideIds || [])}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(joinedRideIds || []),
                )}
              >
                {joinedRideCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">{joinedRideCount}</span>
            )}
          </div>
        )}
        {!!rideTooShortBookingCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-booking"
              title={t("rideProduction.skippedBookingsRideTooShort")}
            >
              {t("rideProduction.skippedBookingsRideTooShort")}:
            </span>
            {rideTooShortBookingIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-orange log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() => navigateToBookingsByIds(rideTooShortBookingIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(rideTooShortBookingIds),
                )}
              >
                {rideTooShortBookingCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-booking">
                {rideTooShortBookingCount}
              </span>
            )}
          </div>
        )}
        {unmatchedBookingsDisplayCount > 0 && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-booking"
              title={t(unmatchedBookingsLabelKey)}
            >
              {t(unmatchedBookingsLabelKey)}:
            </span>
            {unmatchedBookingsDisplayIds.length ? (
              <span
                className="log__metrics-map-value-clickable-orange log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToBookingsByIds(unmatchedBookingsDisplayIds)
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(unmatchedBookingsDisplayIds),
                )}
              >
                {unmatchedBookingsDisplayCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-booking">
                {unmatchedBookingsDisplayCount}
              </span>
            )}
          </div>
        )}
        {!!coursesSkippedOnNoPassengersCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label"
              title={t("rideProduction.coursesSkippedOnNoPassengers")}
            >
              {t("rideProduction.coursesSkippedOnNoPassengers")}:
            </span>
            {coursesSkippedOnNoPassengers?.length ? (
              <span
                className="log__metrics-map-value-clickable-orange"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: coursesSkippedOnNoPassengers,
                    title: t("rideProduction.coursesSkippedOnNoPassengers"),
                  })
                }
                onKeyDown={onActivateKeyDown(() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: coursesSkippedOnNoPassengers,
                    title: t("rideProduction.coursesSkippedOnNoPassengers"),
                  }),
                )}
              >
                {coursesSkippedOnNoPassengersCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">
                {coursesSkippedOnNoPassengersCount}
              </span>
            )}
          </div>
        )}
        {!!coursesAlreadyExistCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label"
              title={t("rideProduction.coursesAlreadyExist")}
            >
              {t("rideProduction.coursesAlreadyExist")}:
            </span>
            {coursesAlreadyExist?.length ? (
              <span
                className="log__metrics-map-value-clickable-orange"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: coursesAlreadyExist,
                    title: t("rideProduction.coursesAlreadyExist"),
                  })
                }
                onKeyDown={onActivateKeyDown(() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: coursesAlreadyExist,
                    title: t("rideProduction.coursesAlreadyExist"),
                  }),
                )}
              >
                {coursesAlreadyExistCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">
                {coursesAlreadyExistCount}
              </span>
            )}
          </div>
        )}
        {!!producedCoursesCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label"
              title={t("rideProduction.producedCourses")}
            >
              {t("rideProduction.producedCourses")}:
            </span>
            {producedCourses?.length ? (
              <span
                className="log__metrics-map-value-clickable-green"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: producedCourses,
                    title: t("rideProduction.producedCourses"),
                  })
                }
                onKeyDown={onActivateKeyDown(() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: producedCourses,
                    title: t("rideProduction.producedCourses"),
                  }),
                )}
              >
                {producedCoursesCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">
                {producedCoursesCount}
              </span>
            )}
          </div>
        )}
        {!!notSavedCoursesCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-error"
              title={t("rideProduction.notSavedCourses")}
            >
              {t("rideProduction.notSavedCourses")}:
            </span>
            {notSavedCourses?.length ? (
              <span
                className="log__metrics-map-value-clickable-red"
                role="button"
                tabIndex={0}
                onClick={() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: notSavedCourses,
                    title: t("rideProduction.notSavedCourses"),
                  })
                }
                onKeyDown={onActivateKeyDown(() =>
                  openCourseSchedulesPreviewDrawer({
                    schedules: notSavedCourses,
                    title: t("rideProduction.notSavedCourses"),
                  }),
                )}
              >
                {notSavedCoursesCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-error">
                {notSavedCoursesCount}
              </span>
            )}
          </div>
        )}
        {!!producedBookingCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              Produced bookings:
            </span>
            {(uniqueProducedBookingIds ?? producedBookingIds)?.length ? (
              <span
                className="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToBookingsByIds(
                    uniqueProducedBookingIds ?? producedBookingIds ?? [],
                  )
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(
                    uniqueProducedBookingIds ?? producedBookingIds ?? [],
                  ),
                )}
              >
                {producedBookingCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-booking">
                {producedBookingCount}
              </span>
            )}
          </div>
        )}
        {!!producedRideCount && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">Processed rides:</span>
            {producedRideIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-green"
                role="button"
                tabIndex={0}
                onClick={() => navigateToRidesByIds(producedRideIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(producedRideIds),
                )}
              >
                {producedRideCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">
                {producedRideCount}
              </span>
            )}
          </div>
        )}
        {!!producedAndJoinedRideIds?.length && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">Total saved rides:</span>
            <span
              className="log__metrics-map-value-clickable-green"
              role="button"
              tabIndex={0}
              onClick={() =>
                navigateToRidesByIds(producedAndJoinedRideIds || [])
              }
              onKeyDown={onActivateKeyDown(() =>
                navigateToRidesByIds(producedAndJoinedRideIds || []),
              )}
            >
              {producedAndJoinedRideIds.length}
            </span>
          </div>
        )}
        {shouldShowJoinDetails && producedRideIds.length > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">
              Produced (generated) rides:
            </span>
            <span
              className="log__metrics-map-value-clickable-green"
              role="button"
              tabIndex={0}
              onClick={() => navigateToRidesByIds(producedRideIds)}
              onKeyDown={onActivateKeyDown(() =>
                navigateToRidesByIds(producedRideIds),
              )}
            >
              {producedRideIds.length}
            </span>
          </div>
        )}
        {shouldShowJoinDetails && !!joinedSentRideSupplierCanceledCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-booking"
              title={t("rideProduction.sentRideSupplierCanceledAtJoin")}
            >
              {t("rideProduction.sentRideSupplierCanceledAtJoin")}:
            </span>
            {joinedSentRideSupplierCanceledIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-orange"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(joinedSentRideSupplierCanceledIds || [])
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(joinedSentRideSupplierCanceledIds || []),
                )}
              >
                {joinedSentRideSupplierCanceledCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">
                {joinedSentRideSupplierCanceledCount}
              </span>
            )}
          </div>
        )}
        {shouldShowJoinDetails && !!joinedSentRideSupplierUpdatedCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-booking"
              title={t("rideProduction.sentRideSupplierUpdatedAtJoin")}
            >
              {t("rideProduction.sentRideSupplierUpdatedAtJoin")}:
            </span>
            {joinedSentRideSupplierUpdatedIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-green"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(joinedSentRideSupplierUpdatedIds || [])
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(joinedSentRideSupplierUpdatedIds || []),
                )}
              >
                {joinedSentRideSupplierUpdatedCount}
              </span>
            ) : (
              <span className="log__metrics-map-value">
                {joinedSentRideSupplierUpdatedCount}
              </span>
            )}
          </div>
        )}
        {shouldShowCourseSupplierSyncStats &&
          !!courseJoinedSentRideSupplierCanceledCount && (
            <div className="log__metrics-map-item">
              <span
                className="log__metrics-map-label log__metrics-map-label-booking"
                title={t(
                  "rideProduction.courseSentRideSupplierCanceledAtProduce",
                )}
              >
                {t("rideProduction.courseSentRideSupplierCanceledAtProduce")}:
              </span>
              {courseJoinedSentRideSupplierCanceledIds?.length ? (
                <span
                  className="log__metrics-map-value-clickable-orange"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigateToRidesByIds(
                      courseJoinedSentRideSupplierCanceledIds || [],
                    )
                  }
                  onKeyDown={onActivateKeyDown(() =>
                    navigateToRidesByIds(
                      courseJoinedSentRideSupplierCanceledIds || [],
                    ),
                  )}
                >
                  {courseJoinedSentRideSupplierCanceledCount}
                </span>
              ) : (
                <span className="log__metrics-map-value">
                  {courseJoinedSentRideSupplierCanceledCount}
                </span>
              )}
            </div>
          )}
        {shouldShowCourseSupplierSyncStats &&
          !!courseJoinedSentRideSupplierUpdatedCount && (
            <div className="log__metrics-map-item">
              <span
                className="log__metrics-map-label log__metrics-map-label-booking"
                title={t(
                  "rideProduction.courseSentRideSupplierUpdatedAtProduce",
                )}
              >
                {t("rideProduction.courseSentRideSupplierUpdatedAtProduce")}:
              </span>
              {courseJoinedSentRideSupplierUpdatedIds?.length ? (
                <span
                  className="log__metrics-map-value-clickable-green"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigateToRidesByIds(
                      courseJoinedSentRideSupplierUpdatedIds || [],
                    )
                  }
                  onKeyDown={onActivateKeyDown(() =>
                    navigateToRidesByIds(
                      courseJoinedSentRideSupplierUpdatedIds || [],
                    ),
                  )}
                >
                  {courseJoinedSentRideSupplierUpdatedCount}
                </span>
              ) : (
                <span className="log__metrics-map-value">
                  {courseJoinedSentRideSupplierUpdatedCount}
                </span>
              )}
            </div>
          )}
      </div>
      <ProductionLogsStatsTabsMetricsHub state={state} />
      <div className="log__metrics-map-stats">
        {!!failedToSaveJoinedRideCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-error"
              title={t("rideProduction.failedToSaveJoinedRides")}
            >
              {t("rideProduction.failedToSaveJoinedRides")}:
            </span>
            {failedToSaveJoinedRideIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-red"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(failedToSaveJoinedRideIds || [])
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(failedToSaveJoinedRideIds || []),
                )}
              >
                {failedToSaveJoinedRideCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-error">
                {failedToSaveJoinedRideCount}
              </span>
            )}
          </div>
        )}
        {!!failedToSaveJoinedBookingCount && (
          <div className="log__metrics-map-item">
            <span
              className="log__metrics-map-label log__metrics-map-label-error"
              title={t("rideProduction.failedToSaveJoinedBookings")}
            >
              {t("rideProduction.failedToSaveJoinedBookings")}:
            </span>
            {failedToSaveJoinedBookingIds?.length ? (
              <span
                className="log__metrics-map-value-clickable-red log__metrics-map-value-booking"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToBookingsByIds(failedToSaveJoinedBookingIds || [])
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(failedToSaveJoinedBookingIds || []),
                )}
              >
                {failedToSaveJoinedBookingCount}
              </span>
            ) : (
              <span className="log__metrics-map-value log__metrics-map-value-error log__metrics-map-value-booking">
                {failedToSaveJoinedBookingCount}
              </span>
            )}
          </div>
        )}
        {!!coursePreSavedRideIds?.length && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">
              {t("rideProduction.preSavedCourses")}:
            </span>
            <span
              className="log__metrics-map-value-clickable-green"
              onClick={() => navigateToRidesByIds(coursePreSavedRideIds)}
            >
              {coursePreSavedRideIds.length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
